import { useCallback, useEffect, useRef, useState } from 'react';
import { AUDIO_HEADER, AUDIO_HEADER_SIZE, SESSION_UUID_BYTES } from '../constants/audioProtocol';

export interface UseAudioCaptureOptions {
  sessionId: string;
  ws: WebSocket | null;
}

export interface UseAudioCaptureReturn {
  startCapture: () => Promise<void>;
  stopCapture: () => void;
  isCapturing: boolean;
  error: string | null;
  /** The AudioContext created during capture, exposed so callers can create
   *  an AnalyserNode from the same context (needed by useVAD). */
  audioContextRef: React.RefObject<AudioContext | null>;
  /** The AnalyserNode connected to the microphone source, exposed for useVAD. */
  analyserRef: React.RefObject<AnalyserNode | null>;
}

/**
 * Converts a hyphenated UUID string (e.g. "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx")
 * to a 16-byte Uint8Array.
 */
function uuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '');
  const bytes = new Uint8Array(SESSION_UUID_BYTES);
  for (let i = 0; i < SESSION_UUID_BYTES; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

/**
 * Builds a binary WebSocket frame for outbound audio:
 *   [0x01 header (1 byte)][session UUID (16 bytes)][PCM Int16 data]
 */
function buildAudioFrame(sessionUuidBytes: Uint8Array, pcm: Int16Array): ArrayBuffer {
  const pcmBytes = new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength);
  const frame = new Uint8Array(AUDIO_HEADER_SIZE + pcmBytes.byteLength);
  frame[0] = AUDIO_HEADER;
  frame.set(sessionUuidBytes, 1);
  frame.set(pcmBytes, AUDIO_HEADER_SIZE);
  return frame.buffer;
}

/**
 * useAudioCapture — requests microphone access, sets up an AudioWorklet for
 * real-time PCM 16-bit 16 kHz mono encoding, and sends binary frames to the
 * backend via WebSocket.
 *
 * Exposes audioContextRef and analyserRef so that sibling hooks (e.g. useVAD)
 * can attach to the same audio graph without requesting a second getUserMedia.
 */
export function useAudioCapture({
  sessionId,
  ws,
}: UseAudioCaptureOptions): UseAudioCaptureReturn {
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const sessionUuidBytesRef = useRef<Uint8Array>(uuidToBytes(sessionId));

  // Keep sessionId bytes up to date if sessionId changes between renders.
  useEffect(() => {
    sessionUuidBytesRef.current = uuidToBytes(sessionId);
  }, [sessionId]);

  const stopCapture = useCallback(() => {
    // Disconnect and tear down the worklet node.
    if (workletNodeRef.current) {
      workletNodeRef.current.port.close();
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    // Stop all microphone tracks.
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        track.stop();
      }
      streamRef.current = null;
    }

    // Close the AudioContext.
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {
        // Best-effort close — ignore errors during cleanup.
      });
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    setIsCapturing(false);
  }, []);

  const startCapture = useCallback(async () => {
    if (isCapturing) return;
    setError(null);

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setError('Microphone permission denied. Please allow microphone access and try again.');
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          setError('No microphone found. Please connect a microphone and try again.');
        } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
          setError('Microphone is already in use by another application.');
        } else {
          setError(`Microphone error: ${err.message}`);
        }
      } else {
        setError('Unexpected error accessing microphone.');
      }
      return;
    }

    streamRef.current = stream;

    // Create AudioContext using the browser's native sample rate.
    const audioContext = new AudioContext();
    audioContextRef.current = audioContext;

    // Build an AnalyserNode for VAD energy analysis (shared with useVAD).
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyserRef.current = analyser;

    // Load the PCM worklet processor module.
    try {
      await audioContext.audioWorklet.addModule(
        new URL('../audio/pcm-processor.worklet.ts', import.meta.url),
      );
    } catch (err) {
      setError('Failed to initialize audio processor. Please refresh and try again.');
      stream.getTracks().forEach((t) => t.stop());
      audioContext.close().catch(() => undefined);
      audioContextRef.current = null;
      return;
    }

    // Wire the microphone source → analyser → worklet node.
    const source = audioContext.createMediaStreamSource(stream);
    const workletNode = new AudioWorkletNode(audioContext, 'pcm-processor');
    workletNodeRef.current = workletNode;

    source.connect(analyser);
    source.connect(workletNode);

    // Receive PCM chunks from the worklet and forward as binary WS frames.
    workletNode.port.onmessage = (event: MessageEvent<{ pcm: Int16Array }>) => {
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      const frame = buildAudioFrame(sessionUuidBytesRef.current, event.data.pcm);
      ws.send(frame);
    };

    setIsCapturing(true);
  }, [isCapturing, ws]);

  // Clean up all audio resources on unmount.
  useEffect(() => {
    return () => {
      stopCapture();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    startCapture,
    stopCapture,
    isCapturing,
    error,
    audioContextRef,
    analyserRef,
  };
}
